import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { ProductCategoriesApiAdapter } from '../adapters/ProductCategoriesApiAdapter';
import { CreateCategoryPayload, DomainErrorBody, UpdateCategoryPayload } from '../models/ProductCategory';
import { PRODUCT_CATEGORIES_QUERY_KEY } from './useProductCategories';

const getDomainErrorMessage = (error: unknown, t: (key: string) => string): string | undefined => {
  const body = (error as { response?: { data?: DomainErrorBody } })?.response?.data;
  const errorCode = body?.error?.error_code;

  if (errorCode) {
    const translationKey = `errors.${errorCode}`;
    const translated = t(translationKey);
    if (translated !== translationKey) return translated;
    return body?.error?.message;
  }

  const laravelMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return laravelMessage ?? (error as Error)?.message;
};

export const useProductCategoryMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('productCategoriesModule');

  const httpClient = createHttpClient(apiClientWH);
  const adapter = new ProductCategoriesApiAdapter(httpClient);

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: [PRODUCT_CATEGORIES_QUERY_KEY] });
  };

  const createCategory = useMutation({
    mutationFn: (payload: CreateCategoryPayload) => adapter.create(payload),
    onSuccess: () => {
      invalidateCategories();
      toast({ title: t('toast.createSuccess') });
    },
    onError: (error: unknown) => {
      toast({
        title: t('toast.errorGeneric'),
        description: getDomainErrorMessage(error, t),
        variant: 'destructive',
      });
    },
  });

  const updateCategory = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateCategoryPayload }) => adapter.update(id, payload),
    onSuccess: () => {
      invalidateCategories();
      toast({ title: t('toast.updateSuccess') });
    },
    onError: (error: unknown) => {
      toast({
        title: t('toast.errorGeneric'),
        description: getDomainErrorMessage(error, t),
        variant: 'destructive',
      });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: (id: number) => adapter.delete(id),
    onSuccess: () => {
      invalidateCategories();
      toast({ title: t('toast.deleteSuccess') });
    },
    onError: (error: unknown) => {
      toast({
        title: t('toast.errorGeneric'),
        description: getDomainErrorMessage(error, t),
        variant: 'destructive',
      });
    },
  });

  return { createCategory, updateCategory, deleteCategory };
};
